import { useEffect, useMemo } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { MdTranslate } from 'react-icons/md';
import { useActiveLocales } from '@features/locales/hooks';
import type { Locale } from '@features/locales/schema';
import { useStateOptions } from '@features/states/hooks';
import type { StateOption } from '@features/states/schema';
import { useDistrictOptions } from '@features/districts/hooks';
import type { DistrictOption } from '@features/districts/schema';
import {
  apmcFormSchema,
  type Apmc,
  type ApmcDetail,
  type ApmcFormTranslation,
  type ApmcFormValues,
} from '../schema';
import { useApmc, useCreateApmc, useUpdateApmc } from '../hooks';

interface Props {
  open: boolean;
  apmc: Apmc | null;
  onClose: () => void;
}

function buildFormTranslations(
  locales: Locale[],
  existing: ApmcDetail['translations'] | undefined,
): ApmcFormTranslation[] {
  const byCode = new Map(existing?.map((t) => [t.localeCode, t]) ?? []);
  return locales
    .filter((l) => !l.isDefault)
    .map((locale) => ({
      localeCode: locale.code,
      localeName: locale.name,
      name: byCode.get(locale.code)?.name ?? '',
    }));
}

function buildEmptyDefaults(
  locales: Locale[],
  existing?: ApmcDetail | null,
): ApmcFormValues {
  return {
    stateId: existing?.stateId ?? '',
    districtId: existing?.districtId ?? '',
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    address: existing?.address ?? null,
    latitude: existing?.latitude ?? null,
    longitude: existing?.longitude ?? null,
    isActive: existing?.isActive ?? true,
    translations: buildFormTranslations(locales, existing?.translations),
  };
}

// Parses an HTML number input back to a number-or-null. Empty string → null.
function parseLatLong(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function ApmcFormDialog({ open, apmc, onClose }: Props) {
  const isEditing = !!apmc;
  const createMutation = useCreateApmc();
  const updateMutation = useUpdateApmc();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const { data: locales = [], isLoading: localesLoading } = useActiveLocales();
  const { data: states = [] } = useStateOptions();
  const { data: districts = [] } = useDistrictOptions();
  const detailQuery = useApmc(apmc?.id ?? null);
  const detail = detailQuery.data ?? null;

  const dataReady = !localesLoading && (!isEditing || detailQuery.isSuccess);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ApmcFormValues>({
    resolver: zodResolver(apmcFormSchema) as Resolver<ApmcFormValues>,
    defaultValues: useMemo(() => buildEmptyDefaults([]), []),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!open || !dataReady) return;
    reset(buildEmptyDefaults(locales, isEditing ? detail : null));
  }, [open, dataReady, locales, detail, isEditing, reset]);

  const stateId = watch('stateId');
  const districtId = watch('districtId');
  const translations = watch('translations');

  const stateById = useMemo(
    () => new Map(states.map((s) => [s.id, s])),
    [states],
  );
  const stateValue: StateOption | null = stateId
    ? stateById.get(stateId) ?? null
    : null;

  // District options scoped to the picked state. Disabled until a state is
  // chosen — same lock-icon-on-disabled pattern as price-series's variety filter.
  const districtOptions = useMemo<DistrictOption[]>(
    () => (stateId ? districts.filter((d) => d.stateId === stateId) : []),
    [districts, stateId],
  );
  const districtById = useMemo(
    () => new Map(districtOptions.map((d) => [d.id, d])),
    [districtOptions],
  );
  const districtValue: DistrictOption | null = districtId
    ? districtById.get(districtId) ?? null
    : null;
  const districtDisabled = !stateId || isEditing;

  const onSubmit = handleSubmit(async (values) => {
    if (isEditing && apmc) {
      await updateMutation.mutateAsync({ id: apmc.id, values });
    } else {
      await createMutation.mutateAsync(values);
    }
    onClose();
  });

  const districtField = (
    <Controller
      control={control}
      name="districtId"
      render={({ field }) => (
        <Autocomplete<DistrictOption>
          size="small"
          options={districtOptions}
          value={districtValue}
          disabled={districtDisabled}
          onChange={(_, opt) => field.onChange(opt ? opt.id : '')}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, v) => opt.id === v.id}
          noOptionsText="No districts in this state"
          renderInput={(p) => (
            <TextField
              {...p}
              label="District"
              error={!!errors.districtId}
              helperText={
                errors.districtId?.message ??
                (isEditing
                  ? 'District is immutable post-create.'
                  : 'Pick the district this APMC sits in.')
              }
              // Lock-icon startAdornment dropped — MUI auto-shrinks the
              // label whenever startAdornment is present, causing visual
              // inconsistency with other empty fields. Disabled state +
              // helperText already communicate the locked-by-cascade case.
            />
          )}
        />
      )}
    />
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit APMC' : 'New APMC'}</DialogTitle>
      <DialogContent dividers>
        {!dataReady ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <form id="apmc-form" onSubmit={onSubmit} className="grid gap-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <Controller
                control={control}
                name="stateId"
                render={({ field }) => (
                  <Autocomplete<StateOption>
                    size="small"
                    options={states}
                    value={stateValue}
                    disabled={isEditing}
                    onChange={(_, opt) => {
                      field.onChange(opt ? opt.id : '');
                      // Picking a new state invalidates the district selection.
                      setValue('districtId', '', {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                    }}
                    getOptionLabel={(opt) => opt.name}
                    isOptionEqualToValue={(opt, v) => opt.id === v.id}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label="State"
                        error={!!errors.stateId}
                        helperText={
                          errors.stateId?.message ??
                          (isEditing
                            ? 'State is immutable post-create.'
                            : 'Pick the state first.')
                        }
                      />
                    )}
                  />
                )}
              />

              {!stateId && !isEditing ? (
                <Tooltip title="Pick a state first to enable District" arrow>
                  <span>{districtField}</span>
                </Tooltip>
              ) : (
                districtField
              )}
            </div>

            <TextField
              label="Code"
              size="small"
              fullWidth
              disabled={isEditing}
              error={!!errors.code}
              helperText={
                errors.code?.message ??
                (isEditing
                  ? 'Code is immutable post-create.'
                  : 'Lowercase alphanumerics joined by hyphens. Unique within the district.')
              }
              {...register('code')}
            />

            <TextField
              label="Name"
              size="small"
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message ?? 'Display name (e.g. "Guntur APMC").'}
              {...register('name')}
            />

            <TextField
              label="Address"
              size="small"
              fullWidth
              multiline
              minRows={2}
              error={!!errors.address}
              helperText={errors.address?.message ?? 'Optional · 500 chars max.'}
              {...register('address', {
                setValueAs: (v: string) => (v === '' ? null : v),
              })}
            />

            <div className="grid grid-cols-2 gap-3">
              <Controller
                control={control}
                name="latitude"
                render={({ field }) => (
                  <TextField
                    label="Latitude"
                    size="small"
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(parseLatLong(e.target.value))}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    slotProps={{
                      htmlInput: {
                        step: '0.000001',
                        min: -90,
                        max: 90,
                        inputMode: 'decimal',
                      },
                    }}
                    error={!!errors.latitude}
                    helperText={errors.latitude?.message ?? '−90 … 90, up to 6 decimals.'}
                  />
                )}
              />
              <Controller
                control={control}
                name="longitude"
                render={({ field }) => (
                  <TextField
                    label="Longitude"
                    size="small"
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(parseLatLong(e.target.value))}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    slotProps={{
                      htmlInput: {
                        step: '0.000001',
                        min: -180,
                        max: 180,
                        inputMode: 'decimal',
                      },
                    }}
                    error={!!errors.longitude}
                    helperText={errors.longitude?.message ?? '−180 … 180, up to 6 decimals.'}
                  />
                )}
              />
            </div>

            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <span className="text-[13px] text-foreground">
                      {field.value
                        ? 'Active — appears in pickers and filters'
                        : 'Inactive — hidden from new attachments; existing references preserved'}
                    </span>
                  }
                />
              )}
            />

            {translations.length > 0 && (
              <div className="grid gap-3">
                <div className="-mb-1 mt-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground-muted">
                  <MdTranslate size={14} />
                  Translations <span className="font-normal normal-case">· optional</span>
                </div>
                {translations.map((t, i) => {
                  const nameErr = errors.translations?.[i]?.name?.message;
                  return (
                    <TextField
                      key={t.localeCode}
                      label={`Name (${t.localeName})`}
                      size="small"
                      fullWidth
                      error={!!nameErr}
                      helperText={nameErr ?? ' '}
                      {...register(`translations.${i}.name` as const)}
                    />
                  );
                })}
              </div>
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="apmc-form"
          variant="contained"
          disabled={submitting || !dataReady}
        >
          {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
